import { Skeleton } from 'antd'
import type { ColumnsType, ColumnType } from 'antd/es/table'
import React from 'react'

import { StyledTable } from 'features/catalog/features/table/catalog-table.styles'
import type { FocusListOption } from 'features/catalog/types'

interface SkeletonRow {
  key: string
}

interface CatalogTableSkeletonProps {
  /** The live column defs — only title / width / align are read, so the
   * skeleton's header can never drift from the real table's (same shape as
   * trend-detection's AgentsTableSkeleton, this feature's closest
   * precedent). */
  columns: ColumnsType<FocusListOption>
  rowCount?: number
}

/**
 * Row-level placeholder for the catalog table: real column chrome (header
 * labels/widths), fake shimmering cells. Rendered instead of the real table
 * while ANY of its data sources (rows, Records counts, Sentences/Sentiment/
 * Rating) are still loading for the current View — never a partial table
 * with 0s that then pops in.
 */
export const CatalogTableSkeleton: React.FC<CatalogTableSkeletonProps> = ({
  columns,
  rowCount = 8,
}) => {
  const skeletonColumns: ColumnsType<SkeletonRow> = columns.map((col, i) => {
    const { title, width, align } = col as ColumnType<FocusListOption>
    // The "#" and action-shaped columns have no/short header text; render no
    // shimmer in their cells so the skeleton doesn't overstate their content.
    const isBareColumn = !title || title === '#'
    return {
      key: `col-${i}`,
      title: title as React.ReactNode,
      width,
      align,
      render: () =>
        isBareColumn ? null : <Skeleton.Input active size="small" block />,
    }
  })

  const skeletonRows: SkeletonRow[] = Array.from(
    { length: rowCount },
    (_, i) => ({ key: `skeleton-${i}` }),
  )

  return (
    <StyledTable
      data-testid="catalog-table-skeleton"
      columns={skeletonColumns}
      dataSource={skeletonRows}
      rowKey="key"
      size="small"
      pagination={false}
    />
  )
}
